/**
 * AccountantSendHistory
 * ---------------------
 * Lists the user's recent sends to the accountant from `accountant_send_log`.
 * Shows status, recipient, invoice count, period, file size and timestamp.
 */
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { History, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const AccountantSendHistory = () => {
  const { user } = useAuth();

  const { data: rows, isLoading } = useQuery({
    queryKey: ["accountant-send-log", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("accountant_send_log")
        .select(
          "id, created_at, recipient_email, invoice_count, send_type, status, file_size_bytes, period_start, period_end, error_message",
        )
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
  });

  const fmtBytes = (b: number | null) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const statusBadge = (status: string) => {
    if (status === "sent")
      return (
        <Badge variant="outline" className="border-primary/40 text-primary">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Trimis
        </Badge>
      );
    if (status === "failed")
      return (
        <Badge variant="outline" className="border-destructive/40 text-destructive">
          <XCircle className="h-3 w-3 mr-1" /> Eșuat
        </Badge>
      );
    return (
      <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
        <Clock className="h-3 w-3 mr-1" /> {status}
      </Badge>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <History className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-semibold">Istoric trimiteri</h3>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : !rows || rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nu ai trimis încă facturi către contabil. Folosește butonul „Trimite la contabil"
          de pe pagina facturii sau de pe Dashboard.
        </p>
      ) : (
        <ul className="divide-y divide-border -mx-2">
          {rows.map((r) => (
            <li key={r.id} className="px-2 py-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="text-sm">
                  <p className="font-medium text-foreground">
                    {r.invoice_count} {r.invoice_count === 1 ? "factură" : "facturi"}
                    {r.send_type === "monthly" && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        · Trimitere lunară
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground break-all">
                    Către: {r.recipient_email}
                  </p>
                </div>
                {statusBadge(r.status)}
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {new Date(r.created_at).toLocaleString("ro-RO", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
                <span>{fmtBytes(r.file_size_bytes)}</span>
              </div>
              {r.error_message && (
                <p className="text-xs text-destructive">{r.error_message}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">
        Linkurile de descărcare expiră după 30 de zile. Arhivele mai vechi sunt șterse automat.
      </p>
    </div>
  );
};

export default AccountantSendHistory;
